import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { keys } from "@/hooks/queries";
import {
  api,
  queryClient,
  type FolderOut,
  type FolderTree,
  type NoteCategory,
  type VaultMoveResult,
} from "@/lib/api";

import { refKey, useFileSelection, type FileRef } from "./file-selection";

export interface MoveTarget {
  /** Null for a PROJECT folder that spans categories — each item is then filed
   * under `folder` within its OWN category, which is the only reading of "put
   * these in Research" that works for a mixed selection. */
  category: NoteCategory | null;
  folder: string;
}

/** Can these items go there at all? A category is intrinsic — dragging a PDF
 * into a Markdown folder cannot turn it into a note — so the UI refuses the
 * drop rather than letting the server reject it after the fact. Exported so the
 * drop target can grey itself out DURING the drag, which is the difference
 * between "this isn't allowed" and "that didn't work". */
export function canMoveInto(refs: FileRef[], target: MoveTarget): boolean {
  if (refs.length === 0) return false;
  // A project folder accepts anything: the item keeps its own category and only
  // its folder changes.
  if (target.category === null) return true;
  return refs.every((r) => r.category === target.category);
}

/** Items that would actually change folder — dropping a file into the folder it
 * is already in is a no-op, and firing a request for it just makes the tree
 * flicker. */
export function movingRefs(refs: FileRef[], target: MoveTarget): FileRef[] {
  return refs.filter(
    (r) =>
      (target.category === null || r.category === target.category) &&
      r.folder !== target.folder,
  );
}

/** Move `refs` inside a cached folder tree, so the optimistic update reflects
 * the destination immediately. Pure — the cache update and the tests share it. */
export function relocateInTree(
  tree: FolderTree,
  refs: FileRef[],
  target: MoveTarget,
): FolderTree {
  const moving = new Set(refs.map(refKey));
  const carried: FolderOut["items"] = [];

  const strip = (folder: FolderOut): FolderOut => {
    const kept = (folder.items ?? []).filter((item) => {
      if (!moving.has(refKey(item))) return true;
      carried.push(item);
      return false;
    });
    return { ...folder, items: kept, children: (folder.children ?? []).map(strip) };
  };

  const place = (folder: FolderOut): FolderOut => {
    // With a shared destination each carried item lands in the folder of that
    // path within ITS OWN category, so the optimistic tree has to match on the
    // item, not on one fixed category.
    const isTarget =
      folder.path === target.folder &&
      (target.category === null || folder.category === target.category);
    return {
      ...folder,
      items: isTarget
        ? [...carried.filter((i) => i.category === folder.category), ...(folder.items ?? [])]
        : (folder.items ?? []),
      children: (folder.children ?? []).map(place),
    };
  };

  const stripped = { ...tree, categories: tree.categories.map(strip) };
  // Counts are derived from items, so recompute rather than patch — a hand-
  // maintained count is exactly the kind of thing that drifts.
  return { ...stripped, categories: stripped.categories.map(place).map(recount) };
}

function recount(folder: FolderOut): FolderOut {
  const children = (folder.children ?? []).map(recount);
  const item_count = (folder.items ?? []).length;
  return {
    ...folder,
    children,
    item_count,
    total_count: item_count + children.reduce((sum, c) => sum + c.total_count, 0),
  };
}

/**
 * Move files into a folder — immediate on screen, durable underneath.
 *
 * The brief's loop, in order:
 *  1. the user drops;
 *  2. `onMutate` re-files the items in the cached tree, so the UI shows the
 *     destination before any request is in flight;
 *  3. the request commits the move in one transaction server-side, and queues
 *     the (expensive) vault relocation behind a coalescing scheduler;
 *  4. `onError` restores the exact previous cache and says what happened —
 *     failures surface rather than leaving the tree quietly wrong;
 *  5. `onSettled` invalidates, so the server reconciles whatever the optimistic
 *     guess got approximately right (counts, ordering, folders that came into
 *     existence).
 *
 * Undo is offered on success because a mis-drop of forty files is otherwise a
 * long afternoon, and the inverse of a move is just another move.
 */
export function useMoveItems() {
  return useMutation({
    mutationFn: async ({ refs, target }: { refs: FileRef[]; target: MoveTarget }) =>
      api.request<VaultMoveResult>("POST", "/api/vault/move", {
        items: refs.map((r) => ({ kind: r.kind, id: r.id })),
        category: target.category,
        folder: target.folder,
        // Trace-only. The server treats a move as idempotent (filing something
        // twice leaves it filed), so this is for reading logs, not correctness.
        request_id: `${Date.now().toString(36)}-${refs.length}`,
      }),

    onMutate: async ({ refs, target }) => {
      await queryClient.cancelQueries({ queryKey: keys.folders });
      const previous = queryClient.getQueryData<FolderTree>(keys.foldersWithItems);
      if (previous) {
        queryClient.setQueryData<FolderTree>(
          keys.foldersWithItems,
          relocateInTree(previous, refs, target),
        );
      }
      // Where each file CAME from, so an undo can put every one back where it
      // was — including a mixed drag out of several different folders.
      const origins = refs.map((r) => ({ ...r }));
      return { previous, origins };
    },

    onError: (error, { refs }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(keys.foldersWithItems, context.previous);
      }
      toast.error(refs.length === 1 ? "Couldn't move that file" : "Couldn't move those files", {
        description: String(error),
      });
    },

    onSuccess: (result, { target }, context) => {
      if (result.rejected.length > 0) {
        // Partial success is a real outcome, not an error: the batch that DID
        // move stays moved, and the user is told precisely what didn't.
        toast.warning(
          `Moved ${result.moved.length}, skipped ${result.rejected.length}`,
          { description: result.rejected[0]?.reason },
        );
      } else if (result.moved.length > 0) {
        const origins = context?.origins ?? [];
        toast.success(
          result.moved.length === 1
            ? `Moved to ${target.folder || "the top level"}`
            : `Moved ${result.moved.length} files to ${target.folder || "the top level"}`,
          {
            duration: 8_000,
            action: origins.length
              ? { label: "Undo", onClick: () => void undoMove(origins) }
              : undefined,
          },
        );
      }
    },

    onSettled: () => {
      // One prefix covers every filesystem view — see hooks/queries.ts.
      void queryClient.invalidateQueries({ queryKey: keys.vault });
      void queryClient.invalidateQueries({ queryKey: ["notes"] });
    },
  });
}

/** Put every file back where it came from. A mixed drag can span several source
 * folders, so this is one request per distinct origin rather than one overall. */
async function undoMove(origins: FileRef[]): Promise<void> {
  // Grouped BY a key but CARRYING the destination, never re-parsed out of it: a
  // folder path holds spaces, slashes and whatever else a user types, so any
  // separator that looked safe here would be a bug waiting for a folder called
  // "Daily notes".
  const groups = new Map<string, { target: MoveTarget; refs: FileRef[] }>();
  for (const ref of origins) {
    const key = JSON.stringify([ref.category, ref.folder]);
    const group = groups.get(key) ?? {
      target: { category: ref.category, folder: ref.folder },
      refs: [],
    };
    group.refs.push(ref);
    groups.set(key, group);
  }
  try {
    for (const { target, refs } of groups.values()) {
      await api.request<VaultMoveResult>("POST", "/api/vault/move", {
        items: refs.map((r) => ({ kind: r.kind, id: r.id })),
        category: target.category,
        folder: target.folder,
      });
    }
    toast("Move undone");
    useFileSelection.getState().clear();
  } catch (error) {
    toast.error("Couldn't undo the move", { description: String(error) });
  } finally {
    void queryClient.invalidateQueries({ queryKey: keys.vault });
    void queryClient.invalidateQueries({ queryKey: ["notes"] });
  }
}
